// EDC easter-egg view, scheduler edition.
//
// This replaces the original "claim a hole" battle layout with a
// dubstep/bass-focused set planner for EDC Las Vegas 2026. Same outer
// shape as the rest of the app (hero card → map → content table → rules)
// so the EDC theme paints into the existing chrome, but content has
// shifted from "claim" to "I'm going to this set" planning.
//
// `territories` is the existing Contentful-backed list of claim submissions;
// here we treat each submission as a user signaling "I want to go" to a
// particular set (matched by id stored in the territory field).
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Button,
  Paper,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Dialog,
  Select,
  MenuItem,
  FormLabel,
  Stack,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import {
  EDC_DAYS,
  EDC_LINEUP,
  dayOrderKey,
  formatSetTime,
  sortedForDay,
} from './edcLineup';

const USER_COLORS = {
  adam: '#9b30ff',
  brandon: '#ff69b4',
  molly: '#00ffff',
};
const FALLBACK_COLOR = '#ffd700';

// Yellow, purple, pink, and cyan from the EDC theme, plus lighter and deeper
// steps of those same hues so each pin can take a distinct ring.
const THEME_MARKER_COLORS = [
  '#ffd400',
  '#9b30ff',
  '#ff2bd6',
  '#00e5ff',
  '#4b0082',
  '#fde68a',
  '#c084fc',
  '#f9a8d4',
  '#67e8f9',
  '#7c3aed',
  '#f59e0b',
  '#e879f9',
];

const markerColorsFor = (tags) => {
  const used = new Set();
  const assigned = {};
  const ordered = [...new Set(
    tags.map((tag) => (tag || '').toString().trim().toLowerCase()).filter(Boolean)
  )].sort();

  ordered.forEach((tag) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i += 1) {
      hash = (hash * 33 + tag.charCodeAt(i)) >>> 0;
    }
    let idx = hash % THEME_MARKER_COLORS.length;
    let guard = 0;
    while (used.has(THEME_MARKER_COLORS[idx]) && guard < THEME_MARKER_COLORS.length) {
      idx = (idx + 1) % THEME_MARKER_COLORS.length;
      guard += 1;
    }
    used.add(THEME_MARKER_COLORS[idx]);
    assigned[tag] = THEME_MARKER_COLORS[idx];
  });

  return assigned;
};

const EDC_MARKER_USERS = [
  'bassbrainbrando',
  'bungeeman',
  'janejams',
  'notrancetrav',
  'villintina',
];

const USER_AVATARS = {
  janejams: 'https://images.ctfassets.net/zw84cuagwed8/29bC7CMypRj3sIpdFmNDny/4d2c58a875bfc2c6d3a5ea587a7b49b5/ChatGPT_Image_Oct_1__2026__03_21_56_PM__4_.png',
  bungeeman: 'https://images.ctfassets.net/zw84cuagwed8/4YlImtO56e0XXROoH4w2Uq/de417da3b9c6921d5f66250a3ec037ea/ChatGPT_Image_Oct_1__2026__03_21_55_PM__1_.png',
  villintina: 'https://images.ctfassets.net/zw84cuagwed8/5yS5iDZEepmF3zwypaMAXX/83a3bb2fb74e8149cb1696ffcac3a170/ChatGPT_Image_Oct_1__2026__03_21_56_PM__5_.png',
  bassbrainbrando: 'https://images.ctfassets.net/zw84cuagwed8/4NScxIBZ8aVCqaqVeZjcRe/2eedd30894b649035f5f51cb6377d0ec/ChatGPT_Image_Oct_1__2026__03_21_55_PM__2_.png',
  notrancetrav: 'https://images.ctfassets.net/zw84cuagwed8/Nlbi3MRaLbtdCb4VL39zy/4d2af3baae814c8eb81c1502e2a06b47/ChatGPT_Image_Oct_1__2026__03_21_56_PM__3_.png',
  lordduffington: 'https://images.ctfassets.net/zw84cuagwed8/59wKW9A9lYwAU6xXLKq1UG/be10a16c7d86a6d5035cacbc5b80c27a/ld.png',
  mulliganmafiaboss: 'https://images.ctfassets.net/zw84cuagwed8/3cb3KtWqwqnWun1sFTNuWa/5bc52d126db87087ee073d2226b7b87d/mmb.png',
  sliceslicebaby420: 'https://images.ctfassets.net/zw84cuagwed8/3LRPDOag5WUU7gPmRgAWir/e3d39573bcffef14e1530620f1d3a3d6/ssb.png',
  bogeymanliveshere: 'https://images.ctfassets.net/zw84cuagwed8/bvWDSteLzTM5EJiMWjcYD/eb4244ee540d0e3fbb651a79d10a5643/ChatGPT_Image_Oct_1__2026__02_28_26_PM__4_.png',
  parthenbarthenjail: 'https://images.ctfassets.net/zw84cuagwed8/7v92RLaTaBTk4wYiWxsACL/2cc6cd12b416d061d91f2bd60b32dce0/ChatGPT_Image_Oct_1__2026__02_28_26_PM__2_.png',
  shankyouverymuch: 'https://images.ctfassets.net/zw84cuagwed8/72zYaf3nceZXlIVq9WpYOd/2713434b22141bcf012607e401a1733c/ChatGPT_Image_Oct_1__2026__02_29_52_PM__1_.png',
  tapintantrum: 'https://images.ctfassets.net/zw84cuagwed8/6n0GB6fHtimHcfoGkDzPix/fae5279f28f8e8f4c3ad7030c01d7abb/ChatGPT_Image_Oct_1__2026__02_29_52_PM__3_.png',
};

const avatarUrl = (tag) =>
  USER_AVATARS[(tag || '').toString().trim().toLowerCase()] || null;

const avatarBackground = (tag, fallback) => {
  const url = avatarUrl(tag);
  if (!url) return { background: fallback };
  return {
    backgroundImage: `url("${url}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
};

// Hype-driven row tint for the Daily Setlist Summary. High hype = warm
// orange/peach; mid = hot magenta; low = deep purple. The curve is a
// piecewise interpolation between three reference colors so the user gets
// a clear "warmer = hotter" read at a glance.
const bgForHype = (avgHype) => {
  const t = Math.max(0, Math.min(10, avgHype)) / 10;
  // Three stops: 0 → purple, 0.5 → magenta, 1 → orange/peach.
  // Interpolate hue + lightness between adjacent stops.
  if (t <= 0.5) {
    const k = t / 0.5; // 0 → 1 across the lower half
    const hue = 270 + (325 - 270) * k;        // 270 → 325
    const lightness = 35 + (55 - 35) * k;     // 35% → 55%
    return `hsl(${hue}, 80%, ${lightness}%)`;
  }
  const k = (t - 0.5) / 0.5; // 0 → 1 across the upper half
  // Wrap from 325 through 360/0 to 22 by going +57° around the wheel.
  const hue = (325 + 57 * k) % 360;
  const lightness = 55 + (65 - 55) * k;       // 55% → 65%
  return `hsl(${hue}, 90%, ${lightness}%)`;
};

// Contentful Delivery API credentials for fetching `edcUserMarker` entries.
// These match the hardcoded values used elsewhere in Blog.js for the other
// Contentful content types (territorySubmission / oceansideClaim /
// edcSetClaim). The previous version pulled these from
// `process.env.REACT_APP_CONTENTFUL_*`, but no .env file ships with the repo
// and netlify.toml defines no build env vars — so SPACE_ID / ACCESS_TOKEN
// were always `undefined`, the fetch effect early-returned, and markers
// never loaded on refresh.
const SPACE_ID = 'zw84cuagwed8';
const ENV_ID = 'master';
const ACCESS_TOKEN = 'V5ofoTzcdDzxrgiKxseGpE-klKXNizC-B--eXor9aI0';

// Re-exported so the existing form import (`EDC_STAGES`) keeps working
// — content-wise we no longer use 9 fixed "stages" as claim slots, but
// we still want to populate the form's stage dropdown.
export const EDC_STAGES = [
  ...new Set(EDC_LINEUP.map((s) => s.stage)),
].map((stage, i) => ({ id: i + 1, name: stage, short: stage }));

const genreLabels = {
  dubstep: 'DUBSTEP',
  riddim:  'RIDDIM',
  bass:    'BASS',
  dnb:     'DNB',
};

const genreColors = {
  dubstep: '#9b30ff',
  riddim:  '#ff2bd6',
  bass:    '#ffd400',
  dnb:     '#00e5ff',
};

const ruleTitles = [
  'Chase the Sunrise',
  'Bass Pod Loyalist',
  'Stage Hopper',
  'B2B Chaser',
  'Headliner Snipe',
  'Three-Night Marathon',
  'Riddim Hour',
];

const rules = [
  'Catch a sunrise set — Above & Beyond, Armin van Buuren, BOLO, or ÆON:MODE. Closing the night = unlocking the morning.',
  'Lock in 5+ sets at Bass Pod across the weekend. Heavy. Filthy. Earned.',
  'Hit 3+ different stages in a single night. Bass Pod → Cosmic Meadow → Kinetic Field, no missed connections.',
  'Catch any back-to-back set: Eptic B2B Space Laces, GRiZ B2B Wooli, or the Doctor P B2B Flux Pavilion B2B FuntCase triple drop.',
  'Snipe one of the heavyweights — Subtronics, Virtual Riot, or Alison Wonderland — and feel the rail buckle.',
  'Lock in at least one set every single night. No fade. No drop missed.',
  'Claim a riddim set after 2am. Wubs only. Sleep is for Monday.',
];

const tipTitles = [
  'Meetup Spot If Lost',
  'Rail Strategy',
  'Screenshot The Schedule',
];

const tips = [
  'Regroup at the Cosmic Meadow.',
  'Get to the rail 90 min before headliners. Bring earplugs (Bass Pod will end you). Pee BEFORE you commit, no second chances.',
  'Service dies at 8pm sharp every night. Screenshot the planner + map before you leave the campsite or you\u2019re flying blind.',
];

function setMatchesTerritory(set, territory) {
  if (!territory) return false;
  return String(territory).toLowerCase() === String(set.id).toLowerCase();
}

export default function BattleForEdcContent({
  territories = [],
  onShowScoreForm,
  onPickSet,
}) {
  const [dayFilter, setDayFilter] = useState('fri');
  const [mapLightboxOpen, setMapLightboxOpen] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [markerDraft, setMarkerDraft] = useState(null);
  const [markerUser, setMarkerUser] = useState('');
  const [markerSaving, setMarkerSaving] = useState(false);
  const [markerError, setMarkerError] = useState('');
  const mapRef = useRef(null);
  const lightboxMapRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Surface a brief "swipe to explore" pill the first ~3s the lightbox is
  // open so mobile users discover that the map can be panned. The CSS
  // keyframe handles the fade; this timer just unmounts the chip after the
  // animation ends to keep the DOM clean.
  useEffect(() => {
    if (!mapLightboxOpen) {
      setShowSwipeHint(false);
      return undefined;
    }
    setShowSwipeHint(true);
    const t = setTimeout(() => setShowSwipeHint(false), 3000);
    return () => clearTimeout(t);
  }, [mapLightboxOpen]);

  useEffect(() => {
    if (!SPACE_ID || !ACCESS_TOKEN) return;
    fetch(
      `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/${ENV_ID}/entries?content_type=edcUserMarker`,
      { headers: { Authorization: `Bearer ${ACCESS_TOKEN}` } }
    )
      .then((r) => r.json())
      .then((data) => {
        if (!data.items) return;
        setMarkers(
          data.items.map((e) => ({
            userTag: e.fields.userTag,
            xPercent: e.fields.xPercent,
            yPercent: e.fields.yPercent,
          }))
        );
      })
      .catch((err) => console.error('Failed to fetch markers', err));
  }, []);

  const closeMarkerPrompt = () => {
    setMarkerDraft(null);
    setMarkerUser('');
    setMarkerError('');
    setMarkerSaving(false);
  };

  const submitMarker = async () => {
    if (!markerDraft || !markerUser) return;
    const userTag = markerUser.trim().toLowerCase();
    const { xPercent, yPercent } = markerDraft;
    setMarkerSaving(true);
    setMarkerError('');
    try {
      const res = await fetch('/.netlify/functions/submit-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: 'marker', userTag, xPercent, yPercent }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMarkerError(err.error || 'Could not save that pin.');
        setMarkerSaving(false);
        return;
      }
      setMarkers((prev) => {
        const filtered = prev.filter((m) => m.userTag !== userTag);
        return [...filtered, { userTag, xPercent, yPercent }];
      });
      closeMarkerPrompt();
    } catch (e) {
      console.error('Marker submit error', e);
      setMarkerError('Could not save that pin.');
      setMarkerSaving(false);
    }
  };

  const openMarkerPrompt = (e, ref) => {
    const img = ref?.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
    if (xPercent < 0 || yPercent < 0 || xPercent > 100 || yPercent > 100) return;
    setMarkerUser('');
    setMarkerError('');
    setMarkerDraft({ xPercent, yPercent });
  };

  const claimsByPlanned = useMemo(() => {
    const map = new Map();
    for (const t of territories) {
      const set = EDC_LINEUP.find((s) => setMatchesTerritory(s, t.territory));
      if (!set) continue;
      const arr = map.get(set.id) || [];
      arr.push(t);
      map.set(set.id, arr);
    }
    return map;
  }, [territories]);

  const hypeStats = useMemo(() => {
    const stats = new Map();
    for (const [setId, claims] of claimsByPlanned.entries()) {
      // Blog.js reshapes the Contentful `hype` field onto each claim as
      // `score` (legacy name carryover from the muni battle context). Fall
      // back to `c.hype` for any pre-reshape data so this stays robust.
      const hypeValues = claims
        .map((c) => (typeof c.score === 'number' ? c.score : c.hype))
        .filter((h) => typeof h === 'number' && !Number.isNaN(h));
      const count = claims.length;
      const avgHype =
        hypeValues.length > 0
          ? hypeValues.reduce((a, b) => a + b, 0) / hypeValues.length
          : 0;
      stats.set(setId, { count, avgHype });
    }
    return stats;
  }, [claimsByPlanned]);

  const isHighHype = (setId) => {
    const stat = hypeStats.get(setId);
    return stat && stat.count >= 2 && stat.avgHype > 7;
  };

  // Per-day "hot sets" rollup feeding the Setlist Summary section. We walk
  // claimsByPlanned (sets that have at least one RSVP) once, attach the
  // lineup metadata + the precomputed { count, avgHype } from hypeStats,
  // bucket by day, then sort: most attendees first, ties broken by avg
  // hype, then earliest start time for a stable ordering.
  const dailySummary = useMemo(() => {
    const byDay = new Map();
    for (const [setId, claims] of claimsByPlanned.entries()) {
      const set = EDC_LINEUP.find((s) => s.id === setId);
      if (!set) continue;
      const stat = hypeStats.get(setId) || { count: claims.length, avgHype: 0 };
      const arr = byDay.get(set.day) || [];
      arr.push({ set, claims, count: stat.count, avgHype: stat.avgHype });
      byDay.set(set.day, arr);
    }
    for (const arr of byDay.values()) {
      arr.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        if (b.avgHype !== a.avgHype) return b.avgHype - a.avgHype;
        return dayOrderKey(a.set) - dayOrderKey(b.set);
      });
    }
    return byDay;
  }, [claimsByPlanned, hypeStats]);

  const hasAnyClaims = claimsByPlanned.size > 0;

  const handleMapClick = (e) => {
    if (isMobile) {
      setMapLightboxOpen(true);
    } else {
      openMarkerPrompt(e, mapRef);
    }
  };

  const handleLightboxMapClick = (e) => {
    openMarkerPrompt(e, lightboxMapRef);
  };

  const markerUsers = useMemo(() => {
    const tags = new Set(EDC_MARKER_USERS);
    for (const t of territories) {
      const tag = (t.battleKey || '').toString().trim().toLowerCase();
      if (tag) tags.add(tag);
    }
    return [...tags].sort();
  }, [territories]);

  const pinColors = useMemo(
    () => markerColorsFor([
      ...markerUsers,
      ...markers.map((m) => m.userTag),
    ]),
    [markerUsers, markers]
  );

  const renderMarkers = () => {
    return markers.map((m) => {
      const key = (m.userTag || '').toString().trim().toLowerCase();
      const color = pinColors[key] || FALLBACK_COLOR;
      const photo = avatarUrl(m.userTag);
      return (
        <Tooltip key={m.userTag} title={m.userTag} arrow>
          <Box
            className="edc-map-marker"
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              left: `${m.xPercent}%`,
              top: `${m.yPercent}%`,
              transform: 'translate(-50%, -50%)',
              width: 36,
              height: 36,
              borderRadius: '50%',
              overflow: 'hidden',
              backgroundColor: color,
              border: `3px solid ${color}`,
              boxShadow: `0 0 12px ${color}`,
              pointerEvents: 'auto',
              cursor: 'default',
              zIndex: 5,
            }}
          >
            {photo && (
              <Box
                component="img"
                src={photo}
                alt={m.userTag}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
        </Tooltip>
      );
    });
  };

  const players = useMemo(() => {
    const counts = {};
    for (const t of territories) {
      if (!t.battleKey) continue;
      const set = EDC_LINEUP.find((s) => setMatchesTerritory(s, t.territory));
      if (!set) continue;
      counts[t.battleKey] = (counts[t.battleKey] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
  }, [territories]);

  const setsForDay = useMemo(() => sortedForDay(dayFilter), [dayFilter]);

  const handlePlanSet = (set) => {
    if (onPickSet) {
      // `onPickSet` (wired to Blog's `openScoreFormWith`) prefills AND
      // opens the dialog in one call. We deliberately do NOT also call
      // `onShowScoreForm` here because that handler resets prefill to
      // null (used by the "Add a Set" footer button for a fresh form),
      // which would wipe the set we just selected.
      onPickSet(set);
      return;
    }
    if (onShowScoreForm) onShowScoreForm();
  };

  return (
    <Grid container spacing={4}>
      <Grid container spacing={4} sx={{ width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: 2,
            width: '100%',
            background: 'rgba(10,0,16,0.78)',
            border: '5px solid #ffd400',
            mb: 0,
          }}
        >
          <Typography
            variant="h2"
            className="edc-title"
            sx={{
              fontSize: { xs: '2.2rem', sm: '4.5rem' },
              mb: 1,
              lineHeight: 1,
              fontWeight: 800,
            }}
          >
            Heavy Rotation HQ
          </Typography>
          <Typography
            variant="subtitle1"
            paragraph
            sx={{
              border: 0,
              fontWeight: 600,
              color: '#ffd400 !important',
              letterSpacing: '0.06em',
            }}
          >
            Lock in your grooves. Bass sets for EDC Las Vegas 2026. Pop a
            bubble or tap any set below to add it to your night. Add your
            location to the map so we can find you!
          </Typography>

          <Tooltip
            title="Click to place a marker"
            arrow
            placement="top"
          >
            <Box
              className="edc-map-hotspots"
              sx={{ position: 'relative', mt: 2, mb: 2, cursor: 'crosshair' }}
            >
              <Box
                ref={mapRef}
                component="img"
                src="/edc-map.png"
                alt="EDC Las Vegas 2026 Festival Map"
                className="edc-map"
                onClick={handleMapClick}
                sx={{
                  transformOrigin: 'center center',
                }}
              />
              {renderMarkers()}
            </Box>
          </Tooltip>

          <Dialog
            open={mapLightboxOpen}
            onClose={() => setMapLightboxOpen(false)}
            fullScreen
            PaperProps={{
              sx: {
                background: 'rgba(10, 0, 16, 0.95)',
                overflow: 'hidden',
              },
            }}
          >
            <IconButton
              onClick={() => setMapLightboxOpen(false)}
              sx={{
                position: 'fixed',
                top: 12,
                right: 12,
                color: '#ffd400',
                background: 'rgba(0,0,0,0.5)',
                zIndex: 20,
                '&:hover': { background: 'rgba(155, 48, 255, 0.6)' },
              }}
              aria-label="Close map"
            >
              <CloseIcon />
            </IconButton>
            {showSwipeHint && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 15,
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  background: 'rgba(10, 0, 16, 0.75)',
                  border: '1px solid #ffd400',
                  color: '#ffd400',
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                  pointerEvents: 'none',
                  animation: 'edc-swipe-hint-fade 3s ease-in-out forwards',
                  '@keyframes edc-swipe-hint-fade': {
                    '0%': { opacity: 0 },
                    '15%': { opacity: 1 },
                    '70%': { opacity: 1 },
                    '100%': { opacity: 0 },
                  },
                }}
              >
                ← swipe to explore →
              </Box>
            )}
            <Box
              sx={{
                width: '100vw',
                height: '100vh',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                bgcolor: '#000',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  minWidth: '100%',
                }}
              >
                <img
                  ref={lightboxMapRef}
                  src="/edc-map.png"
                  alt="EDC Las Vegas 2026 Festival Map"
                  onClick={handleLightboxMapClick}
                  style={{
                    display: 'block',
                    height: 'auto',
                    cursor: 'crosshair',
                  }}
                />
                {renderMarkers()}
              </Box>
            </Box>
          </Dialog>

          <Dialog
            className="score-form"
            open={Boolean(markerDraft)}
            onClose={closeMarkerPrompt}
          >
            <Box
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '320px',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ mt: '-2px' }}>
                  <Typography
                    className="edc-title"
                    sx={{
                      fontFamily: '"Staatliches", "Lexend Deca", sans-serif',
                      fontSize: '0.75rem',
                      letterSpacing: '0.12em',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    HEADBANGER UNION LOCAL 808
                  </Typography>
                </Box>
                <IconButton onClick={closeMarkerPrompt}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="h4" sx={{ textAlign: 'left', letterSpacing: '0.00em' }}>
                Drop your pin
              </Typography>
              <FormLabel htmlFor="marker-user" sx={{ mb: -1 }}>
                Which user?
              </FormLabel>
              <Select
                className="form-input"
                id="marker-user"
                value={markerUser}
                variant="outlined"
                displayEmpty
                onChange={(e) => setMarkerUser(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: '#ffffff',
                      border: '2px solid #ffd400',
                      maxHeight: 280,
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Pick a user</em>
                </MenuItem>
                {markerUsers.map((tag) => {
                  const color = pinColors[tag] || FALLBACK_COLOR;
                  return (
                  <MenuItem key={tag} value={tag}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0,
                          backgroundColor: color,
                          border: `3px solid ${color}`,
                          boxShadow: `0 0 6px ${color}`,
                        }}
                      >
                        {avatarUrl(tag) && (
                          <Box
                            component="img"
                            src={avatarUrl(tag)}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        )}
                      </Box>
                      {tag}
                    </Box>
                  </MenuItem>
                  );
                })}
              </Select>
              {markerError && (
                <Typography sx={{ color: '#ffd400 !important', fontSize: '0.85rem' }}>
                  {markerError}
                </Typography>
              )}
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button variant="outlined" onClick={closeMarkerPrompt}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!markerUser || markerSaving}
                  onClick={submitMarker}
                >
                  {markerSaving ? (
                    <CircularProgress size={16} sx={{ color: 'white !important' }} />
                  ) : (
                    'Drop pin'
                  )}
                </Button>
              </Stack>
            </Box>
          </Dialog>

          {players.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
                gap: 2,
                mb: 4,
                mt: 2,
              }}
            >
              {players.map(({ key, count }) => {
                const userColor =
                  USER_COLORS[key.toLowerCase()] || FALLBACK_COLOR;
                return (
                  <Box
                    key={key}
                    sx={{
                      flex: '1 1 calc(33.333% - 16px)',
                      minWidth: 200,
                      ...(players.length <= 2 && { maxWidth: 250 }),
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <Card
                      className="player-card"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexGrow: 1,
                        }}
                      >
                        <Box
                          className={`bk-${key} avatar`}
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            mr: 2,
                            ...avatarBackground(
                              key,
                              `radial-gradient(circle at 30% 30%, ${userColor}ee 0%, ${userColor} 60%, ${userColor}88 100%)`
                            ),
                            boxShadow: `0 0 14px ${userColor}aa`,
                            border: `2px solid ${userColor}`,
                          }}
                        />
                        <Box>
                          <Typography variant="h6">{key}</Typography>
                          <Typography
                            sx={{ color: 'rgba(255,255,255,0.7) !important' }}
                          >
                            Going to {count} set{count > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>
          )}

          {hasAnyClaims && (
            <Box className="edc-summary-section" sx={{ mb: 4, mt: 1 }}>
              {EDC_DAYS.map((day) => {
                const entries = dailySummary.get(day.id) || [];
                const dayName = day.label.split(' ')[0];
                return (
                  <Box key={day.id} sx={{ mb: 3 }}>
                    <Typography
                      variant="h2"
                      className="edc-title"
                      sx={{
                        fontSize: { xs: '1.6rem', sm: '2.4rem' },
                        mb: 1.5,
                        lineHeight: 1,
                        fontWeight: 800,
                      }}
                    >
                      {dayName} Hot Sets
                    </Typography>
                    {entries.length === 0 ? (
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.55) !important',
                          fontStyle: 'italic',
                          fontSize: '0.85rem',
                          pl: 0.5,
                        }}
                      >
                        No sets locked in for {dayName} yet
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {entries.map(({ set, claims, count, avgHype }) => (
                          <Box
                            key={set.id}
                            className="edc-summary-row"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: { xs: 'wrap', sm: 'nowrap' },
                              gap: { xs: 1, sm: 2 },
                              px: { xs: 1.25, sm: 1.75 },
                              py: { xs: 1, sm: 1.25 },
                              borderRadius: 1.5,
                              border: '1px solid rgba(255,212,0,0.25)',
                              background: bgForHype(avgHype),
                              color: '#ffffff',
                            }}
                          >
                            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: '#ffffff !important',
                                  fontSize: { xs: '0.95rem', sm: '1rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.2,
                                }}
                              >
                                {set.artist}
                              </Typography>
                              <Typography
                                sx={{
                                  color: 'rgba(255,255,255,0.85) !important',
                                  fontSize: '0.72rem',
                                  letterSpacing: '0.02em',
                                }}
                              >
                                {set.stage} · {formatSetTime(set.start)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: { xs: 'flex-start', sm: 'flex-end' },
                                minWidth: { sm: 72 },
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: '#ffffff !important',
                                  fontSize: '0.85rem',
                                  lineHeight: 1.1,
                                }}
                              >
                                {count} going
                              </Typography>
                              <Typography
                                sx={{
                                  color: 'rgba(255,255,255,0.85) !important',
                                  fontSize: '0.68rem',
                                }}
                              >
                                hype {avgHype.toFixed(1)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                flexWrap: 'wrap',
                                flexShrink: 0,
                              }}
                            >
                              {claims.map((c, idx) => {
                                const rawTag = (c.battleKey || '')
                                  .toString()
                                  .trim()
                                  .toLowerCase();
                                const color =
                                  USER_COLORS[rawTag] || FALLBACK_COLOR;
                                return (
                                  <Tooltip
                                    key={`${set.id}-${rawTag || 'anon'}-${idx}`}
                                    title={rawTag || 'anon'}
                                    arrow
                                  >
                                    <Box
                                      className="edc-summary-chip"
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        ...avatarBackground(rawTag, color),
                                        border:
                                          '2px solid rgba(255,255,255,0.65)',
                                        boxShadow: `0 0 6px ${color}`,
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: '#ffd400 !important', mb: 0 }}
            >
              Schedule
            </Typography>
            <ToggleButtonGroup
              value={dayFilter}
              exclusive
              onChange={(_e, val) => val && setDayFilter(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: '#ffd400 !important',
                  borderColor: '#ffd400 !important',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  px: 2,
                },
                '& .Mui-selected': {
                  backgroundColor: 'rgba(155,48,255,0.45) !important',
                  color: '#ffffff !important',
                },
              }}
            >
              {EDC_DAYS.map((d) => (
                <ToggleButton key={d.id} value={d.id}>
                  {d.label.split(' ')[0].slice(0, 3).toUpperCase()}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <TableContainer
            id="edc-table"
            sx={{
              width: '100%',
              overflowX: 'hidden',
              '& .MuiTableCell-root': {
                px: { xs: 0.5, sm: 2 },
                py: { xs: 1.25, sm: 1.5 },
              },
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{ tableLayout: 'fixed', width: '100%' }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 600, width: '18%' }}>
                    Time
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, width: '34%' }}>
                    Artist
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: 600, width: '24%' }}>
                    Stage
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, width: '24%', pr: { xs: 0, sm: 2 } }}
                  >
                    Going
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {setsForDay.map((set) => {
                  const claims = claimsByPlanned.get(set.id) || [];
                  const hype = isHighHype(set.id);
                  return (
                    <TableRow
                      key={set.id}
                      className={hype ? 'edc-hype-row' : undefined}
                    >
                      <TableCell align="left">
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#ffffff !important',
                              fontSize: '0.85rem',
                            }}
                          >
                            {formatSetTime(set.start)}
                          </Typography>
                          <Typography
                            sx={{
                              color: 'rgba(255,255,255,0.55) !important',
                              fontSize: '0.65rem',
                            }}
                          >
                            {set.durationMin}m
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.4,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#ffffff !important',
                              fontSize: '0.9rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {set.artist}
                          </Typography>
                          <Box>
                            <Chip
                              label={genreLabels[set.genre] || set.genre}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                letterSpacing: '0.15em',
                                background:
                                  genreColors[set.genre] || '#ffd400',
                                color: '#0a0010 !important',
                                fontWeight: 800,
                              }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <Typography
                          sx={{
                            color: 'rgba(255,255,255,0.85) !important',
                            fontSize: '0.8rem',
                          }}
                        >
                          {set.stage}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ pr: { xs: 0, sm: 2 } }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Button
                            size="small"
                            variant={claims.length ? 'outlined' : 'contained'}
                            onClick={() => handlePlanSet(set)}
                            sx={{
                              minWidth: { xs: 80, sm: 96 },
                              fontSize: { xs: '0.6875rem', sm: '0.8125rem' },
                            }}
                          >
                            {claims.length ? 'Going +' : "I'm Going"}
                          </Button>
                          {claims.length > 0 && (
                            <Typography
                              sx={{
                                color: 'rgba(255,255,255,0.65) !important',
                                fontSize: '0.65rem',
                              }}
                            >
                              {claims.length} planned
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {setsForDay.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        sx={{ color: 'rgba(255,255,255,0.6) !important' }}
                      >
                        No bass sets locked for this day yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}>
            <Button onClick={onShowScoreForm} size="small" variant="contained">
              Add a Set
            </Button>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            background: 'transparent',
            border: '5px solid transparent',
            p: { xs: 0, sm: 2 },
          }}
        >
          <Typography variant="h2" className="edc-title" sx={{ mb: 2, mt: 3 }}>
            Crew Goals
          </Typography>
          {rules.map((subtitle, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '64px',
                mb: 2,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  mr: 2,
                  fontWeight: 700,
                  lineHeight: 1,
                  fontFamily: 'Inter',
                  minWidth: '40px',
                  textAlign: 'center',
                  borderRadius: '50%',
                  border: '4px solid #ffd400',
                  color: '#ffd400',
                  height: '48px',
                  width: '48px',
                  padding: '4px',
                  fontSize: '1.1rem',
                  flexShrink: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
              </Typography>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'Inter',
                    textTransform: 'uppercase',
                    color: '#ffd400 !important',
                    fontSize: { xs: '0.95rem', sm: '0.875rem', md: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {ruleTitles[i]}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{subtitle}</Typography>
              </Box>
            </Box>
          ))}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            background: 'transparent',
            border: '5px solid transparent',
            p: { xs: 0, sm: 2 },
          }}
        >
          <Typography variant="h2" className="edc-title" sx={{ mb: 2 }}>
            Tips
          </Typography>
          {tips.map((subtitle, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '64px',
                mb: 2,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  mr: 2,
                  fontWeight: 700,
                  lineHeight: 1,
                  fontFamily: 'Inter',
                  minWidth: '40px',
                  textAlign: 'center',
                  borderRadius: '50%',
                  border: '4px solid #ffd400',
                  color: '#ffd400',
                  height: '48px',
                  width: '48px',
                  padding: '4px',
                  fontSize: '1.1rem',
                  flexShrink: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
              </Typography>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'Inter',
                    textTransform: 'uppercase',
                    color: '#ffd400 !important',
                    fontSize: { xs: '0.95rem', sm: '0.875rem', md: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {tipTitles[i]}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{subtitle}</Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      </Grid>
    </Grid>
  );
}
